import { AxiosError } from "axios";
import { isAppUpdateAvailable } from "@/features/app-version/stores/appVersionStore";

export const APP_UPDATE_ERROR_MESSAGE =
  "Tem uma versão nova da plataforma. Recarregue a página e tente de novo.";

/**
 * Mensagens genéricas em inglês que o backend devolve quando NÃO houve exceção
 * de domínio: o mapa de reason phrases do `sendClientError` (405 do router, 404
 * de rota inexistente) e o mascaramento de 5xx. São contrato HTTP, não copy —
 * nunca vão pra tela (regra: nada de inglês cru pro usuário). Ficam no log.
 */
const GENERIC_BACKEND_MESSAGES = new Set([
  "Internal server error",
  "Request error",
  "Invalid request",
  "Bad Request",
  "Unauthorized",
  "Forbidden",
  "Not Found",
  "Method Not Allowed",
  "Not Acceptable",
  "Conflict",
  "Gone",
  "Payload Too Large",
  "Unsupported Media Type",
  "Unprocessable Entity",
  "Too Many Requests",
]);

/**
 * Extrai a mensagem de erro da resposta da API.
 *
 * São dois formatos em jogo: o do Strapi cru (`{ error: { message } }`, vindo de
 * plugins e do content-manager) e o do nosso `send()`/`handleApplicationError`
 * (`{ success, code, message }`), que é o das rotas próprias. Ler só o primeiro
 * fazia TODA mensagem de 4xx das nossas rotas cair no texto genérico: o cliente
 * via "Tente novamente" sem saber que o problema era, por exemplo, o tamanho de
 * um campo (SUP do onboarding: 78 falhas em 30 dias sem pista na tela).
 */
export function getRequestErrorMessage(
  error: unknown,
  defaultMessage: string = "Ocorreu um erro inesperado."
): string {
  // SA-1416: com a versão nova no ar, o primeiro passo certo para qualquer
  // falha de REQUEST é recarregar (o contrato pode ter mudado). Erro que não é
  // de request não muda com reload; 401 fica de fora porque já desloga. A
  // mensagem original segue no log de quem chamou.
  if (
    isAppUpdateAvailable() &&
    error instanceof AxiosError &&
    error.response?.status !== 401
  ) {
    return APP_UPDATE_ERROR_MESSAGE;
  }

  if (error instanceof AxiosError) {
    const data = error.response?.data;
    const message = data?.error?.message ?? data?.message;

    if (
      typeof message === "string" &&
      message.trim() &&
      !GENERIC_BACKEND_MESSAGES.has(message.trim())
    ) {
      return message;
    }
  }

  return defaultMessage;
}
